/**
 * CoachPlaybook - Timeline Scrubber Component
 * Version: 0.6.0
 * Description: Video-editor-style timeline bar for play step navigation,
 *              scrubbing, reordering, and playback speed control.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';

/* ───────── SVG Icon Components ───────── */

const PlayIcon = ({ size = 16, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M8 5v14l11-7z" />
  </svg>
);

const PauseIcon = ({ size = 16, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <rect x="6" y="4" width="4" height="16" rx="1" />
    <rect x="14" y="4" width="4" height="16" rx="1" />
  </svg>
);

const StepBackIcon = ({ size = 14, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <rect x="4" y="5" width="3" height="14" rx="1" />
    <path d="M20 5v14l-11-7z" />
  </svg>
);

const StepForwardIcon = ({ size = 14, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <rect x="17" y="5" width="3" height="14" rx="1" />
    <path d="M4 5v14l11-7z" />
  </svg>
);

const AddIcon = ({ size = 16, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RemoveIcon = ({ size = 12, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/* ───────── Style Definitions ───────── */

const buildStyles = (accentColor) => {
  const accent = accentColor || '#3b82f6';

  return {
    container: {
      position: 'relative',
      width: '100%',
      height: 60,
      background: 'rgba(15, 23, 42, 0.92)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      borderTop: '1px solid rgba(255,255,255,0.06)',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      userSelect: 'none',
      zIndex: 50,
    },
    upper: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      padding: '0 8px',
      gap: 6,
    },
    /* ── Transport controls (left) ── */
    transport: {
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexShrink: 0,
    },
    transportBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: 6,
      border: 'none',
      background: 'transparent',
      cursor: 'pointer',
      transition: 'background 0.15s',
    },
    playBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 32,
      height: 32,
      borderRadius: 8,
      border: `1.5px solid ${accent}`,
      background: 'rgba(255,255,255,0.04)',
      cursor: 'pointer',
      transition: 'all 0.15s',
    },
    /* ── Frame strip (center) ── */
    frameArea: {
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      position: 'relative',
      margin: '0 6px',
    },
    frameStrip: {
      display: 'flex',
      alignItems: 'center',
      gap: 3,
      overflowX: 'auto',
      overflowY: 'hidden',
      scrollbarWidth: 'none',
      padding: '2px 4px',
    },
    frame: (active) => ({
      position: 'relative',
      width: 40,
      height: 34,
      minWidth: 40,
      borderRadius: 5,
      border: active ? `2px solid ${accent}` : '1.5px solid rgba(255,255,255,0.1)',
      background: active ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
      boxShadow: active ? `0 0 8px ${accent}44` : 'none',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.15s',
      flexShrink: 0,
    }),
    frameNumber: (active) => ({
      fontSize: 10,
      fontWeight: active ? 700 : 500,
      color: active ? '#fff' : 'rgba(255,255,255,0.5)',
      lineHeight: 1,
      letterSpacing: 0.3,
    }),
    frameDots: {
      display: 'flex',
      gap: 2,
      marginTop: 3,
      flexWrap: 'wrap',
      justifyContent: 'center',
      maxWidth: 34,
    },
    frameDot: (color) => ({
      width: 4,
      height: 4,
      borderRadius: '50%',
      background: color || 'rgba(255,255,255,0.3)',
    }),
    frameRemove: {
      position: 'absolute',
      top: -4,
      right: -4,
      width: 14,
      height: 14,
      borderRadius: '50%',
      background: '#ef4444',
      border: '1.5px solid rgba(15,23,42,0.9)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      opacity: 0,
      transition: 'opacity 0.12s',
      zIndex: 2,
    },
    /* ── Drag preview ── */
    dragGhost: {
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: 9999,
      opacity: 0.85,
    },
    dropIndicator: {
      width: 2,
      height: 30,
      background: accent,
      borderRadius: 1,
      flexShrink: 0,
      alignSelf: 'center',
    },
    /* ── Right controls ── */
    rightControls: {
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      flexShrink: 0,
    },
    speedControl: {
      display: 'flex',
      alignItems: 'center',
      gap: 3,
    },
    speedLabel: {
      fontSize: 10,
      fontWeight: 600,
      color: 'rgba(255,255,255,0.55)',
      letterSpacing: 0.4,
      minWidth: 28,
      textAlign: 'center',
      cursor: 'pointer',
      padding: '2px 4px',
      borderRadius: 4,
      transition: 'background 0.12s',
    },
    speedBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 18,
      height: 18,
      borderRadius: 4,
      border: '1px solid rgba(255,255,255,0.12)',
      background: 'transparent',
      color: 'rgba(255,255,255,0.5)',
      fontSize: 12,
      fontWeight: 700,
      cursor: 'pointer',
      lineHeight: 1,
      padding: 0,
      transition: 'all 0.12s',
    },
    addBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: 6,
      border: `1.5px dashed rgba(255,255,255,0.15)`,
      background: 'transparent',
      cursor: 'pointer',
      transition: 'all 0.15s',
    },
    /* ── Progress bar (bottom) ── */
    progressTrack: {
      position: 'relative',
      width: '100%',
      height: 6,
      background: 'rgba(255,255,255,0.06)',
      cursor: 'pointer',
      flexShrink: 0,
    },
    progressFill: (progress) => ({
      position: 'absolute',
      left: 0,
      top: 0,
      height: '100%',
      width: `${(progress || 0) * 100}%`,
      background: `linear-gradient(90deg, ${accent}, ${accent}cc)`,
      transition: 'width 0.06s linear',
      pointerEvents: 'none',
    }),
    progressHandle: (progress) => ({
      position: 'absolute',
      top: -3,
      left: `calc(${(progress || 0) * 100}% - 5px)`,
      width: 10,
      height: 12,
      borderRadius: 3,
      background: '#fff',
      boxShadow: `0 0 6px ${accent}88, 0 1px 3px rgba(0,0,0,0.4)`,
      cursor: 'grab',
      transition: 'left 0.06s linear',
      zIndex: 2,
    }),
    /* ── Tooltip ── */
    tooltip: {
      position: 'absolute',
      bottom: '110%',
      left: '50%',
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#fff',
      fontSize: 10,
      fontWeight: 600,
      padding: '3px 6px',
      borderRadius: 4,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 10,
    },
    /* ── Divider ── */
    divider: {
      width: 1,
      height: 20,
      background: 'rgba(255,255,255,0.08)',
      flexShrink: 0,
    },
  };
};

/* ───────── Speed Presets ───────── */

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3];

const nextSpeed = (current, direction) => {
  const idx = SPEEDS.indexOf(current);
  if (idx === -1) return 1;
  const next = idx + direction;
  if (next < 0 || next >= SPEEDS.length) return current;
  return SPEEDS[next];
};

const formatSpeed = (s) => `${s}x`;

/* ───────── Helper: extract player dots from a step ───────── */

const getStepPlayerDots = (step) => {
  if (!step) return [];
  const dots = [];

  // step.players or step.actions — adapt to common play data shapes
  const players = step.players || step.actions || [];
  if (Array.isArray(players)) {
    players.forEach((p) => {
      const color = p.color || p.positionColor || null;
      if (color) dots.push(color);
    });
  }

  // If step has a playerPositions map
  if (step.playerPositions && typeof step.playerPositions === 'object') {
    Object.values(step.playerPositions).forEach((pos) => {
      if (pos.color) dots.push(pos.color);
    });
  }

  return dots.slice(0, 7); // cap at 7 dots to avoid overflow
};

/* ═══════════════════════════════════════════
   TimelineScrubber Component
   ═══════════════════════════════════════════ */

const TimelineScrubber = ({
  steps = [],
  currentStep = 0,
  totalSteps = 0,
  isPlaying = false,
  animationProgress = 0,
  speed = 1,
  onStepChange,
  onPlayPause,
  onSpeedChange,
  onAddStep,
  onRemoveStep,
  onProgressScrub,
  accentColor,
}) => {
  const S = buildStyles(accentColor);
  const frameStripRef = useRef(null);
  const progressRef = useRef(null);

  /* ── Hover / tooltip state ── */
  const [hoveredFrame, setHoveredFrame] = useState(null);
  const [hoverRemove, setHoverRemove] = useState(null);

  /* ── Drag-to-reorder state ── */
  const [dragIndex, setDragIndex] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const dragStartRef = useRef(null);

  /* ── Progress scrub state ── */
  const [isScrubbing, setIsScrubbing] = useState(false);

  /* ── Transport hover states ── */
  const [hoverTransport, setHoverTransport] = useState(null);
  const [hoverPlay, setHoverPlay] = useState(false);
  const [hoverAdd, setHoverAdd] = useState(false);
  const [hoverSpeedBtn, setHoverSpeedBtn] = useState(null);

  const stepCount = totalSteps || steps.length || 0;

  /* ── Auto-scroll active frame into view ── */
  useEffect(() => {
    if (frameStripRef.current) {
      const active = frameStripRef.current.querySelector('[data-active="true"]');
      if (active) {
        active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [currentStep]);

  /* ── Progress bar scrub handlers ── */
  const handleProgressDown = useCallback((e) => {
    e.preventDefault();
    setIsScrubbing(true);
    const rect = progressRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    onProgressScrub?.(pct);
  }, [onProgressScrub]);

  useEffect(() => {
    if (!isScrubbing) return;

    const onMove = (e) => {
      const rect = progressRef.current?.getBoundingClientRect();
      if (!rect) return;
      const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      onProgressScrub?.(pct);
    };

    const onUp = () => setIsScrubbing(false);

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isScrubbing, onProgressScrub]);

  /* ── Frame drag-to-reorder handlers ── */
  const handleFrameDragStart = useCallback((e, idx) => {
    e.preventDefault();
    setDragIndex(idx);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    setDragPos({ x: e.clientX, y: e.clientY });
  }, []);

  useEffect(() => {
    if (dragIndex === null) return;

    const onMove = (e) => {
      setDragPos({ x: e.clientX, y: e.clientY });

      // Determine drop target from frame strip children
      if (frameStripRef.current) {
        const children = Array.from(frameStripRef.current.children).filter(
          (c) => c.dataset.frameIdx !== undefined
        );
        let target = null;
        children.forEach((child) => {
          const rect = child.getBoundingClientRect();
          const mid = rect.left + rect.width / 2;
          if (e.clientX > mid) {
            target = parseInt(child.dataset.frameIdx, 10) + 1;
          }
        });
        if (target === null) target = 0;
        if (target === dragIndex || target === dragIndex + 1) target = null;
        setDropTarget(target);
      }
    };

    const onUp = () => {
      if (dropTarget !== null && dragIndex !== null && onStepChange) {
        // Compute the final index after removal
        const finalIdx = dropTarget > dragIndex ? dropTarget - 1 : dropTarget;
        if (finalIdx !== dragIndex) {
          onStepChange(finalIdx);
        }
      }
      setDragIndex(null);
      setDropTarget(null);
      dragStartRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragIndex, dropTarget, onStepChange]);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    const handleKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          onPlayPause?.();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          if (currentStep > 0) onStepChange?.(currentStep - 1);
          break;
        case 'ArrowRight':
          e.preventDefault();
          if (currentStep < stepCount - 1) onStepChange?.(currentStep + 1);
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [currentStep, stepCount, onPlayPause, onStepChange]);

  /* ── Render helpers ── */

  const renderFrame = (step, idx) => {
    const active = idx === currentStep;
    const isHovered = hoveredFrame === idx;
    const dots = getStepPlayerDots(step);
    const isDragging = dragIndex === idx;

    const frameStyle = {
      ...S.frame(active),
      ...(isHovered && !active ? { border: '1.5px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.06)' } : {}),
      ...(isDragging ? { opacity: 0.3 } : {}),
    };

    return (
      <div
        key={`frame-${idx}`}
        data-frame-idx={idx}
        data-active={active ? 'true' : 'false'}
        style={frameStyle}
        onClick={() => onStepChange?.(idx)}
        onMouseDown={(e) => handleFrameDragStart(e, idx)}
        onMouseEnter={() => setHoveredFrame(idx)}
        onMouseLeave={() => { setHoveredFrame(null); setHoverRemove(null); }}
      >
        {/* Step number */}
        <span style={S.frameNumber(active)}>{idx}</span>

        {/* Player dots */}
        {dots.length > 0 && (
          <div style={S.frameDots}>
            {dots.map((c, i) => (
              <div key={i} style={S.frameDot(c)} />
            ))}
          </div>
        )}

        {/* Remove button on hover */}
        {stepCount > 1 && onRemoveStep && (
          <div
            style={{
              ...S.frameRemove,
              ...(isHovered ? { opacity: 1 } : {}),
            }}
            onMouseEnter={() => setHoverRemove(idx)}
            onMouseLeave={() => setHoverRemove(null)}
            onClick={(e) => {
              e.stopPropagation();
              onRemoveStep(idx);
            }}
          >
            <RemoveIcon size={8} color="#fff" />
          </div>
        )}

        {/* Tooltip */}
        {isHovered && !isDragging && (
          <div style={S.tooltip}>Step {idx + 1}</div>
        )}
      </div>
    );
  };

  /* ── Main render ── */

  return (
    <div style={S.container} data-element="timeline-scrubber">
      {/* Upper row: transport | frames | right controls */}
      <div style={S.upper}>
        {/* Transport controls */}
        <div style={S.transport}>
          <button
            style={{
              ...S.transportBtn,
              ...(hoverTransport === 'back' ? { background: 'rgba(255,255,255,0.08)' } : {}),
            }}
            onClick={() => currentStep > 0 && onStepChange?.(currentStep - 1)}
            onMouseEnter={() => setHoverTransport('back')}
            onMouseLeave={() => setHoverTransport(null)}
            title="Previous step"
          >
            <StepBackIcon size={13} color={currentStep > 0 ? '#fff' : 'rgba(255,255,255,0.25)'} />
          </button>

          <button
            style={{
              ...S.playBtn,
              ...(hoverPlay ? { background: 'rgba(255,255,255,0.08)', transform: 'scale(1.05)' } : {}),
            }}
            onClick={() => onPlayPause?.()}
            onMouseEnter={() => setHoverPlay(true)}
            onMouseLeave={() => setHoverPlay(false)}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <PauseIcon size={15} /> : <PlayIcon size={15} />}
          </button>

          <button
            style={{
              ...S.transportBtn,
              ...(hoverTransport === 'fwd' ? { background: 'rgba(255,255,255,0.08)' } : {}),
            }}
            onClick={() => currentStep < stepCount - 1 && onStepChange?.(currentStep + 1)}
            onMouseEnter={() => setHoverTransport('fwd')}
            onMouseLeave={() => setHoverTransport(null)}
            title="Next step"
          >
            <StepForwardIcon size={13} color={currentStep < stepCount - 1 ? '#fff' : 'rgba(255,255,255,0.25)'} />
          </button>
        </div>

        <div style={S.divider} />

        {/* Frame strip */}
        <div style={S.frameArea}>
          <div
            ref={frameStripRef}
            style={{
              ...S.frameStrip,
              /* hide scrollbar in WebKit */
              msOverflowStyle: 'none',
            }}
          >
            {steps.map((step, idx) => {
              const elements = [];

              // Drop indicator before this frame
              if (dropTarget === idx) {
                elements.push(<div key={`drop-${idx}`} style={S.dropIndicator} />);
              }

              elements.push(renderFrame(step, idx));

              // Drop indicator after last frame
              if (idx === steps.length - 1 && dropTarget === steps.length) {
                elements.push(<div key="drop-end" style={S.dropIndicator} />);
              }

              return elements;
            })}

            {/* Empty state */}
            {steps.length === 0 && (
              <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, padding: '0 8px' }}>
                No steps
              </div>
            )}
          </div>
        </div>

        <div style={S.divider} />

        {/* Right controls: speed + add */}
        <div style={S.rightControls}>
          {/* Speed control */}
          <div style={S.speedControl}>
            <button
              style={{
                ...S.speedBtn,
                ...(hoverSpeedBtn === 'dec' ? { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)' } : {}),
              }}
              onClick={() => onSpeedChange?.(nextSpeed(speed, -1))}
              onMouseEnter={() => setHoverSpeedBtn('dec')}
              onMouseLeave={() => setHoverSpeedBtn(null)}
              title="Decrease speed"
            >
              -
            </button>
            <span
              style={{
                ...S.speedLabel,
                ...(hoverSpeedBtn === 'label' ? { background: 'rgba(255,255,255,0.06)' } : {}),
              }}
              onClick={() => onSpeedChange?.(1)}
              onMouseEnter={() => setHoverSpeedBtn('label')}
              onMouseLeave={() => setHoverSpeedBtn(null)}
              title="Reset to 1x"
            >
              {formatSpeed(speed)}
            </span>
            <button
              style={{
                ...S.speedBtn,
                ...(hoverSpeedBtn === 'inc' ? { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)' } : {}),
              }}
              onClick={() => onSpeedChange?.(nextSpeed(speed, 1))}
              onMouseEnter={() => setHoverSpeedBtn('inc')}
              onMouseLeave={() => setHoverSpeedBtn(null)}
              title="Increase speed"
            >
              +
            </button>
          </div>

          <div style={S.divider} />

          {/* Add step button */}
          {onAddStep && (
            <button
              style={{
                ...S.addBtn,
                ...(hoverAdd ? { border: '1.5px dashed rgba(255,255,255,0.3)', background: 'rgba(255,255,255,0.04)' } : {}),
              }}
              onClick={() => onAddStep?.()}
              onMouseEnter={() => setHoverAdd(true)}
              onMouseLeave={() => setHoverAdd(false)}
              title="Add step"
            >
              <AddIcon size={14} color="rgba(255,255,255,0.5)" />
            </button>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div
        ref={progressRef}
        style={S.progressTrack}
        onMouseDown={handleProgressDown}
      >
        <div style={S.progressFill(animationProgress)} />
        <div
          style={{
            ...S.progressHandle(animationProgress),
            ...(isScrubbing ? { cursor: 'grabbing', transform: 'scale(1.2)' } : {}),
          }}
        />
      </div>

      {/* Drag ghost */}
      {dragIndex !== null && (
        <div
          style={{
            ...S.dragGhost,
            left: dragPos.x - 20,
            top: dragPos.y - 17,
          }}
        >
          <div style={S.frame(true)}>
            <span style={S.frameNumber(true)}>{dragIndex + 1}</span>
          </div>
        </div>
      )}

      {/* Global style injection for scrollbar hiding */}
      <style>{`
        [data-element="timeline-scrubber"] div::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default TimelineScrubber;
